import React, { useEffect, useMemo, useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ResponsiveBar } from "@nivo/bar";
import { API_BASE_URL } from "../../config";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppDivider from "../../design-system/components/AppDivider";
import AppField from "../../design-system/components/AppField";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppInput from "../../design-system/components/AppInput";
import AppSelect from "../../design-system/components/AppSelect";
import AppSwitch from "../../design-system/components/AppSwitch";
import AppTable from "../../design-system/components/AppTable";
import AppTablePane from "../../design-system/components/AppTablePane";
import AppText from "../../design-system/components/AppText";
import AppToolbar from "../../design-system/components/AppToolbar";
import AppTooltip from "../../design-system/components/AppTooltip";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import { IconChevronDown, IconChevronRight } from "../../design-system/icons";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./laborCosts.css";

const TIMEFRAME_OPTIONS = ["day", "week", "month", "custom"];

function toYmd(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseYmd(ymd) {
  if (!ymd) return null;
  const parts = ymd.split("-").map(Number);
  if (parts.length !== 3) return null;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getWeekBounds(anchorDate) {
  const anchor = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate());
  const weekday = anchor.getDay();
  const mondayDiff = weekday === 0 ? -6 : 1 - weekday;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() + mondayDiff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: monday, end: sunday };
}

function getMonthBounds(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex + 1, 0);
  return { start: first, end: last };
}

function resolveRange(timeframe, refDateYmd, year, monthIndex, customStartYmd, customEndYmd) {
  if (timeframe === "day") {
    const d = parseYmd(refDateYmd);
    if (!d) return null;
    const ymd = toYmd(d);
    return { start_date: ymd, end_date: ymd };
  }

  if (timeframe === "week") {
    const d = parseYmd(refDateYmd);
    if (!d) return null;
    const { start, end } = getWeekBounds(d);
    return { start_date: toYmd(start), end_date: toYmd(end) };
  }

  if (timeframe === "month") {
    const y = Number(year);
    const m = Number(monthIndex);
    if (Number.isNaN(y) || Number.isNaN(m)) return null;
    const { start, end } = getMonthBounds(y, m);
    return { start_date: toYmd(start), end_date: toYmd(end) };
  }

  const customStart = parseYmd(customStartYmd);
  const customEnd = parseYmd(customEndYmd);
  if (!customStart || !customEnd || customEnd < customStart) return null;
  return { start_date: toYmd(customStart), end_date: toYmd(customEnd) };
}

function formatHours(value) {
  return `${Number(value || 0).toFixed(2)} h`;
}

function LaborCostsPage() {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const token = localStorage.getItem("session_token");

  const now = useMemo(() => new Date(), []);
  const todayYmd = useMemo(() => toYmd(now), [now]);

  const [timeframe, setTimeframe] = useState("month");
  const [referenceDate, setReferenceDate] = useState(todayYmd);
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [customStart, setCustomStart] = useState(toYmd(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [customEnd, setCustomEnd] = useState(toYmd(new Date(now.getFullYear(), now.getMonth() + 1, 0)));

  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareTimeframe, setCompareTimeframe] = useState("month");
  const [compareReferenceDate, setCompareReferenceDate] = useState(todayYmd);
  const [compareMonth, setCompareMonth] = useState(now.getMonth());
  const [compareYear, setCompareYear] = useState(now.getFullYear());
  const [compareCustomStart, setCompareCustomStart] = useState(toYmd(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [compareCustomEnd, setCompareCustomEnd] = useState(toYmd(new Date(now.getFullYear(), now.getMonth() + 1, 0)));

  const [isLoading, setIsLoading] = useState(true);
  const [primaryData, setPrimaryData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [errorText, setErrorText] = useState("");
  const [expandedDepartments, setExpandedDepartments] = useState({});

  const monthOptions = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language || "de-CH", { month: "long" });
    return [...Array(12)].map((_, idx) => ({
      value: idx,
      label: formatter.format(new Date(2026, idx, 1)),
    }));
  }, [i18n.language]);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1, y + 2];
  }, [now]);

  const primaryRange = useMemo(
    () => resolveRange(timeframe, referenceDate, year, month, customStart, customEnd),
    [timeframe, referenceDate, year, month, customStart, customEnd]
  );

  const secondaryRange = useMemo(
    () =>
      compareEnabled
        ? resolveRange(
            compareTimeframe,
            compareReferenceDate,
            compareYear,
            compareMonth,
            compareCustomStart,
            compareCustomEnd
          )
        : null,
    [
      compareEnabled,
      compareTimeframe,
      compareReferenceDate,
      compareYear,
      compareMonth,
      compareCustomStart,
      compareCustomEnd,
    ]
  );

  const moneyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language || "de-CH", {
        style: "currency",
        currency: "CHF",
        maximumFractionDigits: 2,
      }),
    [i18n.language]
  );

  const FALLBACK_PALETTE = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.primary.dark,
      theme.palette.info.dark,
      theme.palette.success.dark,
      theme.palette.warning.dark,
    ],
    [theme]
  );

  const departmentColorMap = useMemo(
    () => primaryData?.department_color_map || compareData?.department_color_map || {},
    [primaryData, compareData]
  );

  const getDeptColor = useMemo(() => {
    const norm = {};
    Object.entries(departmentColorMap || {}).forEach(([k, v]) => {
      const key = String(k || "").trim().toLowerCase();
      if (key && v) norm[key] = v;
    });

    const hashIndex = (str) => {
      let h = 0;
      for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
      return h % FALLBACK_PALETTE.length;
    };

    return (department) => {
      const key = String(department || "").trim().toLowerCase();
      if (key && norm[key]) return norm[key];
      if (key) return FALLBACK_PALETTE[hashIndex(key)];
      return alpha(theme.palette.text.primary, 0.18);
    };
  }, [departmentColorMap, FALLBACK_PALETTE, theme]);

  useEffect(() => {
    if (!primaryRange) {
      setErrorText(t("labor_costs.invalid_range"));
      return;
    }
    if (compareEnabled && !secondaryRange) {
      setErrorText(t("labor_costs.invalid_range"));
      return;
    }

    setErrorText("");
    setIsLoading(true);

    const headers = { Authorization: `Bearer ${token}` };
    const primaryPromise = axios.get(`${API_BASE_URL}/api/report/labor_costs`, {
      headers,
      params: primaryRange,
    });

    const comparePromise =
      compareEnabled && secondaryRange
        ? axios.get(`${API_BASE_URL}/api/report/labor_costs`, {
            headers,
            params: secondaryRange,
          })
        : Promise.resolve(null);

    Promise.all([primaryPromise, comparePromise])
      .then(([primaryRes, compareRes]) => {
        setPrimaryData(primaryRes.data);
        setCompareData(compareRes?.data || null);
      })
      .catch(() => {
        setErrorText(t("labor_costs.load_error"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, primaryRange, secondaryRange, compareEnabled, t]);

  const compareDepartmentMap = useMemo(() => {
    const map = new Map();
    (compareData?.departments || []).forEach((d) => map.set(d.department, d));
    return map;
  }, [compareData]);

  const departmentRows = useMemo(
    () =>
      (primaryData?.departments || []).map((dept) => {
        const compareDept = compareDepartmentMap.get(dept.department);
        const compareCost = Number(compareDept?.cost || 0);
        return {
          id: dept.department,
          department: dept.department,
          hours: Number(dept.hours || 0),
          cost: Number(dept.cost || 0),
          compare_cost: compareCost,
          delta_cost: Number(dept.cost || 0) - compareCost,
          shifts: dept.shifts || [],
        };
      }),
    [primaryData, compareDepartmentMap]
  );

  const gridRows = useMemo(() => {
    const rows = [];
    for (const dept of departmentRows) {
      rows.push({
        id: `dept:${dept.id}`,
        row_type: "department",
        department: dept.department,
        shift: dept.department,
        hours: dept.hours,
        cost: dept.cost,
        compare_cost: dept.compare_cost,
        delta_cost: dept.delta_cost,
      });

      if (expandedDepartments[dept.id]) {
        const compareDept = compareDepartmentMap.get(dept.department);
        for (const shift of dept.shifts || []) {
          const compareShift = (compareDept?.shifts || []).find(
            (s) =>
              String(s.shift_id ?? "") === String(shift.shift_id ?? "") &&
              s.shift_name === shift.shift_name
          );
          const shiftDelta = Number(shift.cost || 0) - Number(compareShift?.cost || 0);

          rows.push({
            id: `shift:${dept.id}:${shift.shift_id ?? "none"}:${shift.shift_name}`,
            row_type: "shift",
            parent_department: dept.id,
            shift: shift.shift_name,
            hours: Number(shift.hours || 0),
            cost: Number(shift.cost || 0),
            compare_cost: Number(compareShift?.cost || 0),
            delta_cost: shiftDelta,
          });
        }
      }
    }
    return rows;
  }, [departmentRows, expandedDepartments, compareDepartmentMap]);

  const costKey = t("labor_costs.cost");
  const compareCostKey = t("labor_costs.compare_cost");

  const chartData = useMemo(
    () =>
      departmentRows.map((row) => ({
        department: row.department,
        [costKey]: row.cost,
        ...(compareEnabled ? { [compareCostKey]: row.compare_cost } : {}),
      })),
    [departmentRows, compareEnabled, costKey, compareCostKey]
  );

  const chartKeys = useMemo(
    () => (compareEnabled ? [costKey, compareCostKey] : [costKey]),
    [compareEnabled, costKey, compareCostKey]
  );

  const chartMetrics = useMemo(() => {
    const count = Math.max(chartData.length, 1);
    const keyCount = Math.max(chartKeys.length, 1);
    const sideMargin = 84;
    const slotWidth = keyCount === 1 ? 36 : 46;
    const minPlotWidth = Math.max(count * slotWidth + sideMargin, 300);

    return {
      minPlotWidth,
      padding: keyCount === 1 ? 0.54 : 0.34,
      innerPadding: keyCount === 1 ? 1 : 6,
    };
  }, [chartData.length, chartKeys.length]);

  const totalHours = Number(primaryData?.totals?.hours || 0);
  const totalCost = Number(primaryData?.totals?.cost || 0);

  const costShareRows = useMemo(() => {
    if (!departmentRows.length || totalCost <= 0) return [];
    return departmentRows
      .map((row) => ({
        department: row.department,
        hours: row.hours,
        cost: row.cost,
        percent: (row.cost / totalCost) * 100,
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [departmentRows, totalCost]);

  const DonutPie = ({ items, total, getColor }) => {
    const size = 200;
    const cx = size / 2;
    const cy = size / 2;
    const outerR = 96;
    const innerR = 58;

    const normalized = (Array.isArray(items) ? items : []).filter((x) => x && typeof x.percent === "number");
    const arcs = [];
    let startAngle = -Math.PI / 2;

    if (total <= 0 || normalized.length === 0) {
      arcs.push({ path: describeArc(cx, cy, outerR, innerR, 0, Math.PI * 2), color: alpha(theme.palette.text.primary, 0.12) });
    } else {
      normalized.forEach((seg) => {
        const frac = Math.max(0, seg.percent) / 100;
        const angle = frac * Math.PI * 2;
        const endAngle = startAngle + angle;
        arcs.push({
          path: describeArc(cx, cy, outerR, innerR, startAngle, endAngle),
          color: getColor?.(seg.department) ?? alpha(theme.palette.text.primary, 0.12),
        });
        startAngle = endAngle;
      });
    }

    return (
      <div className="labor-costs-donut">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="department-cost-donut">
          {arcs.map((a, idx) => (
            <path key={`${a.color}-${idx}`} d={a.path} fill={a.color} />
          ))}
          <circle cx={cx} cy={cy} r={innerR - 2} fill={theme.palette.background.box} />
          <text
            x={cx}
            y={cy - 2}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontWeight: 800, fontSize: 18, fill: theme.palette.text.primary }}
          >
            {Math.round(total)}
          </text>
          <text
            x={cx}
            y={cy + 18}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: 11, fill: theme.palette.text.secondary }}
          >
            CHF
          </text>
        </svg>
      </div>
    );
  };

  function describeArc(cx, cy, outerR, innerR, startAngle, endAngle) {
    const polar = (ang, r) => [cx + r * Math.cos(ang), cy + r * Math.sin(ang)];
    const [x0o, y0o] = polar(startAngle, outerR);
    const [x1o, y1o] = polar(endAngle, outerR);
    const [x0i, y0i] = polar(endAngle, innerR);
    const [x1i, y1i] = polar(startAngle, innerR);
    const largeArc = Math.abs(endAngle - startAngle) > Math.PI ? 1 : 0;

    return [
      `M ${x0o} ${y0o}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x1o} ${y1o}`,
      `L ${x0i} ${y0i}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x1i} ${y1i}`,
      "Z",
    ].join(" ");
  }

  const columns = [
    {
      key: "shift",
      label: t("labor_costs.department"),
      grow: 1.3,
      minWidth: 260,
      sortable: false,
      render: (value, row) => {
        const isDept = row.row_type === "department";
        if (isDept) {
          const deptId = row.department;
          const isOpen = !!expandedDepartments[deptId];
          const hasShifts = (departmentRows.find((d) => d.department === deptId)?.shifts || []).length > 0;
          return (
            <span className="labor-costs-table__department">
              {hasShifts ? (
                <AppTooltip content={isOpen ? t("labor_costs.collapse") : t("labor_costs.expand")}>
                  <AppButton
                    variant="muted"
                    size="xs"
                    iconOnly
                    aria-label={isOpen ? t("labor_costs.collapse") : t("labor_costs.expand")}
                    onClick={() =>
                      setExpandedDepartments((prev) => ({
                        ...prev,
                        [deptId]: !prev[deptId],
                      }))
                    }
                  >
                    {isOpen ? <IconChevronDown sx={{ fontSize: 18 }} /> : <IconChevronRight sx={{ fontSize: 18 }} />}
                  </AppButton>
                </AppTooltip>
              ) : (
                <span className="labor-costs-table__expand-placeholder" aria-hidden="true" />
              )}
              <AppText variant="heading-4" truncate>{value}</AppText>
            </span>
          );
        }

        return <AppText className="labor-costs-table__shift" tone="secondary" truncate>{value}</AppText>;
      },
    },
    {
      key: "hours",
      label: t("labor_costs.hours"),
      grow: 0.7,
      minWidth: 130,
      align: "right",
      render: (value) => <AppText>{formatHours(value)}</AppText>,
    },
    {
      key: "cost",
      label: t("labor_costs.cost"),
      grow: 0.8,
      minWidth: 150,
      align: "right",
      render: (value) => <AppText>{moneyFormatter.format(Number(value || 0))}</AppText>,
    },
    ...(compareEnabled
      ? [
          {
            key: "compare_cost",
            label: t("labor_costs.compare_cost"),
            grow: 0.8,
            minWidth: 150,
            align: "right",
            render: (value) => <AppText>{moneyFormatter.format(Number(value || 0))}</AppText>,
          },
          {
            key: "delta_cost",
            label: t("labor_costs.delta_cost"),
            grow: 0.8,
            minWidth: 150,
            align: "right",
            render: (value) => <AppText>{moneyFormatter.format(Number(value || 0))}</AppText>,
          },
        ]
      : []),
  ];

  const timeframeOptions = TIMEFRAME_OPTIONS.map((option) => ({
    value: option,
    label: t(`labor_costs.${option}`),
  }));
  const normalizedYearOptions = yearOptions.map((option) => ({ value: option, label: String(option) }));

  const renderPeriodFields = ({
    timeframeValue,
    onTimeframeChange,
    referenceDateValue,
    onReferenceDateChange,
    monthValue,
    onMonthChange,
    yearValue,
    onYearChange,
    customStartValue,
    onCustomStartChange,
    customEndValue,
    onCustomEndChange,
    timeframeLabel,
  }) => {
    const fields = [
      <AppField key="timeframe" label={timeframeLabel}>
        <AppSelect value={timeframeValue} options={timeframeOptions} onChange={onTimeframeChange} />
      </AppField>,
    ];

    if (timeframeValue === "day" || timeframeValue === "week") {
      fields.push(
        <AppField key="date" label={t("labor_costs.date")}>
          <AppInput type="date" value={referenceDateValue} onChange={onReferenceDateChange} />
        </AppField>
      );
    }

    if (timeframeValue === "month") {
      fields.push(
          <AppField key="month" label={t("labor_costs.month")}>
            <AppSelect value={monthValue} options={monthOptions} onChange={onMonthChange} />
          </AppField>,
          <AppField key="year" label={t("labor_costs.year")}>
            <AppSelect value={yearValue} options={normalizedYearOptions} onChange={onYearChange} />
          </AppField>
      );
    }

    if (timeframeValue === "custom") {
      fields.push(
          <AppField key="start" label={t("labor_costs.start_date")}>
            <AppInput type="date" value={customStartValue} onChange={onCustomStartChange} />
          </AppField>,
          <AppField key="end" label={t("labor_costs.end_date")}>
            <AppInput type="date" value={customEndValue} onChange={onCustomEndChange} />
          </AppField>
      );
    }

    return fields;
  };

  const primaryControls = (
    <AppToolbar
      className="labor-costs-toolbar"
      left={(
        <AppFieldGrid columns={4} className="labor-costs-toolbar__fields">
          {renderPeriodFields({
            timeframeValue: timeframe,
            onTimeframeChange: (event) => setTimeframe(event.target.value),
            referenceDateValue: referenceDate,
            onReferenceDateChange: (event) => setReferenceDate(event.target.value),
            monthValue: month,
            onMonthChange: (event) => setMonth(Number(event.target.value)),
            yearValue: year,
            onYearChange: (event) => setYear(Number(event.target.value)),
            customStartValue: customStart,
            onCustomStartChange: (event) => setCustomStart(event.target.value),
            customEndValue: customEnd,
            onCustomEndChange: (event) => setCustomEnd(event.target.value),
            timeframeLabel: t("labor_costs.timeframe"),
          })}
        </AppFieldGrid>
      )}
      right={(
        <AppSwitch
          checked={compareEnabled}
          onChange={(event) => setCompareEnabled(event.target.checked)}
          label={t("labor_costs.compare_mode")}
        />
      )}
    />
  );

  return (
    <div className="labor-costs-page">
      <AppWorkspace
        title={t("labor_costs.title")}
        subtitle={t("labor_costs.subtitle")}
        loading={isLoading}
      >
        {primaryControls}

        {errorText ? <AppCallout variant="error" description={errorText} /> : null}
        {!errorText && primaryData?.has_hourly_wage === false ? (
          <AppCallout variant="info" description={t("labor_costs.no_hourly_wage_defined")} />
        ) : null}

        {compareEnabled ? (
          <AppCard>
            <AppCardHeader title={t("labor_costs.compare_timeframe")} titleVariant="compact" />
            <AppCardBody>
              <AppFieldGrid columns={4} className="labor-costs-compare-fields">
                {renderPeriodFields({
                  timeframeValue: compareTimeframe,
                  onTimeframeChange: (event) => setCompareTimeframe(event.target.value),
                  referenceDateValue: compareReferenceDate,
                  onReferenceDateChange: (event) => setCompareReferenceDate(event.target.value),
                  monthValue: compareMonth,
                  onMonthChange: (event) => setCompareMonth(Number(event.target.value)),
                  yearValue: compareYear,
                  onYearChange: (event) => setCompareYear(Number(event.target.value)),
                  customStartValue: compareCustomStart,
                  onCustomStartChange: (event) => setCompareCustomStart(event.target.value),
                  customEndValue: compareCustomEnd,
                  onCustomEndChange: (event) => setCompareCustomEnd(event.target.value),
                  timeframeLabel: t("labor_costs.compare_timeframe"),
                })}
              </AppFieldGrid>
            </AppCardBody>
          </AppCard>
        ) : null}

        <div className="labor-costs-summary-grid">
          <AppCard className="labor-costs-summary-card">
            <AppCardHeader title={t("labor_costs.cost_share_by_department")} titleVariant="compact" />
            <AppCardBody className="labor-costs-summary-card__body">
              <DonutPie items={costShareRows} total={totalCost} getColor={getDeptColor} />
            </AppCardBody>
          </AppCard>

          <AppCard className="labor-costs-summary-card">
            <AppCardHeader title={t("labor_costs.department_cost_overview")} titleVariant="compact" />
            <AppCardBody className="labor-costs-summary-card__body labor-costs-summary-card__body--overview">
              <div className="labor-costs-department-list">
                {costShareRows.length ? (
                  costShareRows.map((row) => {
                    const col = getDeptColor(row.department);
                    return (
                      <div key={row.department} className="labor-costs-department-list__row">
                        <span className="labor-costs-department-list__dot" style={{ backgroundColor: col }} aria-hidden="true" />
                        <AppText truncate title={row.department}>{row.department}</AppText>
                        <AppText tone="secondary">{`${Math.round(row.percent)}%`}</AppText>
                        <AppText tone="secondary">{formatHours(row.hours)}</AppText>
                      </div>
                    );
                  })
                ) : (
                  <AppText tone="secondary">{t("labor_costs.no_rows")}</AppText>
                )}
              </div>
              <div className="labor-costs-department-list__total">
                <AppDivider />
                <AppText variant="heading-4">
                  {`${t("labor_costs.total_cost")}: ${moneyFormatter.format(totalCost)}`}
                </AppText>
              </div>
            </AppCardBody>
          </AppCard>

          <AppCard className="labor-costs-summary-card labor-costs-summary-card--chart">
            <AppCardHeader title={t("labor_costs.expense_by_department")} titleVariant="compact" />
            <AppCardBody className="labor-costs-summary-card__body">
              <div className="labor-costs-chart">
                {chartData.length ? (
                  <div style={{ width: `max(100%, ${chartMetrics.minPlotWidth}px)`, height: "100%" }}>
                    <ResponsiveBar
                      data={chartData}
                      keys={chartKeys}
                      indexBy="department"
                      margin={{ top: 10, right: 14, bottom: 56, left: 70 }}
                      padding={chartMetrics.padding}
                      innerPadding={chartMetrics.innerPadding}
                      groupMode="grouped"
                      valueScale={{ type: "linear" }}
                      indexScale={{ type: "band", round: true }}
                      colors={(bar) =>
                        bar.id === compareCostKey
                          ? alpha(getDeptColor(bar.indexValue), 0.45)
                          : getDeptColor(bar.indexValue)
                      }
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ tickSize: 4, tickPadding: 4, tickRotation: -16 }}
                      axisLeft={{
                        tickSize: 4,
                        tickPadding: 4,
                        tickRotation: 0,
                        legend: t("labor_costs.cost"),
                        legendPosition: "middle",
                        legendOffset: -52,
                      }}
                      enableLabel={false}
                      role="application"
                      barAriaLabel={(e) => `${e.id}: ${e.formattedValue} ${e.indexValue}`}
                    />
                  </div>
                ) : (
                  <AppText tone="secondary">{t("labor_costs.no_rows")}</AppText>
                )}
              </div>
            </AppCardBody>
          </AppCard>
        </div>

        <AppTablePane className={`labor-costs-table-pane${compareEnabled ? " is-comparing" : ""}`}>
          <AppTable
            rows={gridRows}
            columns={columns}
            getRowId={(row) => row.id}
            getRowClassName={(row) => (row.row_type === "shift" ? "labor-costs-table__shift-row" : "")}
            density="compact"
            loading={isLoading}
            emptyState={t("labor_costs.no_rows")}
            footer={(
              <>
                <AppTable.FooterMeta>
                  <span>{`${t("labor_costs.total_hours")}: ${formatHours(totalHours)}`}</span>
                </AppTable.FooterMeta>
                <AppTable.FooterSummary>
                  <span>{`${t("labor_costs.total_cost")}: ${moneyFormatter.format(totalCost)}`}</span>
                </AppTable.FooterSummary>
              </>
            )}
          />
        </AppTablePane>
      </AppWorkspace>
    </div>
  );
}

export default LaborCostsPage;
